import * as React from 'react';

import { getLocalTimeZone, today } from '@internationalized/date';
import { cva } from 'class-variance-authority';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Calendar as AriaCalendar,
  CalendarCell as AriaCalendarCell,
  CalendarCellProps as AriaCalendarCellProps,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody as AriaCalendarGridBody,
  CalendarGridBodyProps as AriaCalendarGridBodyProps,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarGridHeaderProps as AriaCalendarGridHeaderProps,
  CalendarGridProps as AriaCalendarGridProps,
  CalendarHeaderCell as AriaCalendarHeaderCell,
  CalendarHeaderCellProps as AriaCalendarHeaderCellProps,
  CalendarProps as AriaCalendarProps,
  DateValue as AriaDateValue,
  CalendarCellRenderProps,
  Heading as AriaHeading,
  RangeCalendar as AriaRangeCalendar,
  RangeCalendarProps as AriaRangeCalendarProps,
  RangeCalendarStateContext as AriaRangeCalendarStateContext,
  composeRenderProps,
  Text,
  useLocale,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { Button } from '../buttons/Button';

const Calendar = AriaCalendar;

const RangeCalendar = AriaRangeCalendar;

/**
 * Calendar heading component for displaying month/year navigation.
 * Includes previous and next buttons for navigation.
 *
 * @param props - Calendar heading HTML element properties
 */
const CalendarHeading = (props: React.HTMLAttributes<HTMLElement>) => {
  const { direction } = useLocale();

  return (
    <header
      className="@CalendarHeading flex w-full items-center gap-1 px-1 pb-4"
      {...props}
    >
      <Button slot="previous" variant="outline" size="small">
        {direction === 'rtl' ? (
          <ChevronRight aria-hidden className="size-4" />
        ) : (
          <ChevronLeft aria-hidden className="size-4" />
        )}
      </Button>
      <AriaHeading className="grow text-center text-sm font-medium" />
      <Button slot="next" variant="outline" size="small">
        {direction === 'rtl' ? (
          <ChevronLeft aria-hidden className="size-4" />
        ) : (
          <ChevronRight aria-hidden className="size-4" />
        )}
      </Button>
    </header>
  );
};

/**
 * Calendar grid component for displaying dates in a table layout.
 *
 * @param props - Calendar grid properties
 */
const CalendarGrid = ({ className, ...props }: AriaCalendarGridProps) => (
  <AriaCalendarGrid
    className={cn(
      '@CalendarGrid',
      'border-separate border-spacing-x-0 border-spacing-y-1',
      className,
    )}
    {...props}
  />
);

/**
 * Calendar grid header component for displaying day names.
 *
 * @param props - Calendar grid header properties
 */
const CalendarGridHeader = ({ ...props }: AriaCalendarGridHeaderProps) => (
  <AriaCalendarGridHeader {...props} className="@CalendarGridHeader" />
);

/**
 * Calendar header cell component for displaying individual day names in the header.
 *
 * @param props - Calendar header cell properties
 */
const CalendarHeaderCell = ({
  className,
  ...props
}: AriaCalendarHeaderCellProps) => (
  <AriaCalendarHeaderCell
    className={cn(
      '@CalendarHeaderCell',
      'w-9 rounded-md text-[0.8rem] font-normal text-neutral/20',
      className,
    )}
    {...props}
  />
);

/**
 * Calendar grid body component for displaying weeks and days.
 *
 * @param props - Calendar grid body properties
 */
const CalendarGridBody = ({
  className,
  ...props
}: AriaCalendarGridBodyProps) => (
  <AriaCalendarGridBody
    className={cn('@CalendarGridBody', '[&>tr>td]:p-0', className)}
    {...props}
  />
);

const calendarCellVariants = cva(
  [
    'relative flex size-9 cursor-default items-center justify-center rounded-md p-0 text-sm font-normal',
    'border border-transparent text-neutral/90 outline-[3px] outline-transparent transition-colors',
    /* Selected */
    'rac-selected:bg-primary rac-selected:text-white',
    /* Hovered | Focused */
    'rac-hover:border-primary/20 rac-hover:bg-primary/20 rac-hover:text-primary rac-focus:border-primary/20 rac-focus:bg-primary/20 rac-focus:text-primary',
    /* (Hovered | Focused) & Selected */
    'rac-selected:rac-hover:bg-primary/50 rac-selected:rac-hover:text-white rac-selected:rac-focus:outline-primary/20',
    /* Selection */
    'rac-selection-start:rounded-r-none',
    'rac-selection-end:rounded-l-none',
    'rac-selection-start:rac-selection-end:rounded-md',
    'rac-disabled:cursor-not-allowed rac-disabled:opacity-30',
  ],
  {
    variants: {
      isRangeMiddle: {
        true: '!bg-primary/5 !text-neutral/80 not-rac-selection-start:not-rac-selection-end:rounded-none',
      },
      isOutsideMonth: {
        true: 'text-neutral/60 opacity-50 rac-selected:bg-primary/50 rac-selected:text-neutral/60 rac-selected:opacity-30',
      },
      isToday: {
        true: 'font-medium text-primary',
      },
      isUnavailable: {
        true: 'cursor-default text-failure',
      },
      isInvalid: {
        true: 'bg-failure text-failure/20 rac-hover:bg-failure rac-focus:bg-failure',
      },
    },
    defaultVariants: {},
  },
);

/**
 * Calendar cell component for displaying and selecting individual dates.
 * Handles styling for various states including selection, hover, today, and range selection.
 *
 * @param props - Calendar cell properties
 */
const CalendarCell = ({ className, ...props }: AriaCalendarCellProps) => {
  const range = React.useContext(AriaRangeCalendarStateContext);
  const isRange = Boolean(range);

  return (
    <AriaCalendarCell
      className={composeRenderProps(
        cn('@CalendarCell', className),
        (className, renderProps: CalendarCellRenderProps) => {
          const isRangeMiddle =
            renderProps.isSelected &&
            isRange &&
            !renderProps.isSelectionStart &&
            !renderProps.isSelectionEnd;

          const isToday =
            renderProps.date.compare(today(getLocalTimeZone())) === 0 &&
            !renderProps.isSelected;

          return cn(
            calendarCellVariants({
              isRangeMiddle,
              isOutsideMonth: renderProps.isOutsideMonth,
              isToday,
              isUnavailable: renderProps.isUnavailable,
              isInvalid: renderProps.isInvalid,
            }),
            className,
          );
        },
      )}
      {...props}
    />
  );
};

/**
 * Props for the CalendarField component.
 * Extends AriaCalendarProps to add error handling.
 */
interface CalendarFieldProps<
  T extends AriaDateValue,
> extends AriaCalendarProps<T> {
  /**
   * Error message to display when validation fails
   */
  errorMessage?: string;
}

/**
 * Complete calendar field component with built-in header, grid, and error handling.
 * Provides a fully functional calendar for single date selection.
 *
 * @example
 * ```tsx
 * <CalendarField
 *   aria-label="Select a date"
 *   defaultValue={today(getLocalTimeZone())}
 *   onChange={date => console.log(date.toString())}
 * />
 * ```
 *
 * @param props - Calendar field properties
 */
const CalendarField = <T extends AriaDateValue>({
  errorMessage,
  className,
  ...props
}: CalendarFieldProps<T>) => (
  <Calendar
    className={composeRenderProps(
      cn('@CalendarField', className),
      (className) => cn('w-fit', className),
    )}
    {...props}
  >
    <CalendarHeading />
    <CalendarGrid>
      <CalendarGridHeader>
        {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
      </CalendarGridHeader>
      <CalendarGridBody>
        {(date) => <CalendarCell date={date} />}
      </CalendarGridBody>
    </CalendarGrid>
    {errorMessage && (
      <Text className="text-sm !text-failure" slot="errorMessage">
        {errorMessage}
      </Text>
    )}
  </Calendar>
);

/**
 * Props for the RangeCalendarField component.
 * Extends AriaRangeCalendarProps to add error handling.
 */
interface RangeCalendarFieldProps<
  T extends AriaDateValue,
> extends AriaRangeCalendarProps<T> {
  /**
   * Error message to display when validation fails
   */
  errorMessage?: string;
}

/**
 * Complete range calendar field component with built-in header, grid, and error handling.
 * Provides a fully functional calendar for date range selection.
 *
 * @example
 * ```tsx
 * <RangeCalendarField
 *   aria-label="Select date range"
 *   onChange={range => console.log(range.start.toString(), range.end.toString())}
 * />
 * ```
 *
 * @param props - Range calendar field properties
 */
const RangeCalendarField = <T extends AriaDateValue>({
  errorMessage,
  className,
  ...props
}: RangeCalendarFieldProps<T>) => (
  <RangeCalendar
    className={composeRenderProps(
      cn('@RangeCalendarField', className),
      (className) => cn('w-fit', className),
    )}
    {...props}
  >
    <CalendarHeading />
    <CalendarGrid>
      <CalendarGridHeader>
        {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
      </CalendarGridHeader>
      <CalendarGridBody>
        {(date) => <CalendarCell date={date} />}
      </CalendarGridBody>
    </CalendarGrid>
    {errorMessage && (
      <Text className="text-sm !text-failure" slot="errorMessage">
        {errorMessage}
      </Text>
    )}
  </RangeCalendar>
);

export {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarHeading,
  RangeCalendar,
  CalendarField,
  RangeCalendarField,
};
export type { CalendarFieldProps, RangeCalendarFieldProps };
